// Helper for the mobile relationship-mapping WebView (my.happily.ai/relationship-mapping?code=).
//
// The WebView has no Firebase session, so it cannot use the auth-required cache APIs. This
// calls relationshipMappingApi (happily-functions), which decrypts the `code` server-side,
// reads /users + /rel_maps via the Admin SDK, and returns only the rendered fields. The code
// is the bearer credential, so no auth header is needed and RTDB can stay locked.
import axios from 'axios'

const REL_MAPPING_API =
  'https://us-central1-people2-admin.cloudfunctions.net/relationshipMappingApi'

// v2 (token-secured). Used when the new mobile app injects a Firebase ID token into the WebView.
const REL_MAPPING_V2_API =
  'https://us-central1-people2-admin.cloudfunctions.net/relationshipMappingV2Api'

export interface RelationshipMappingResult {
  email: string
  user: {
    firstname: string
    lastname: string
    profilepic: string
    profilepic_crop: string
    profilepic_small: string
    house: string
    company: string
  }
  relMaps: any
}

// v1: returns the user + rel_maps for the given code, or null on error / invalid code.
export async function fetchRelationshipMapping(
  code: string
): Promise<RelationshipMappingResult | null> {
  try {
    const res = await axios.post(REL_MAPPING_API, { code }, { timeout: 15000 })
    return (res && res.data) ? res.data : null
  } catch (e) {
    console.error('[relationshipMappingApi] fetch failed', e)
    return null
  }
}

// v2: token-secured. `token` is the caller's Firebase ID token (injected by the new mobile
// app); `target` is the email of the profile being viewed (optional — defaults to the caller's
// own map server-side). The function authorizes by the token's company claim. Returns the same
// shape as v1, or null on error.
export async function fetchRelationshipMappingV2(
  token: string,
  target?: string
): Promise<RelationshipMappingResult | null> {
  try {
    const params: any = {}
    if (target) params.target = target
    const res = await axios.get(REL_MAPPING_V2_API, {
      params,
      headers: { Authorization: `Bearer ${token}` },
      timeout: 15000,
    })
    return (res && res.data) ? res.data : null
  } catch (e) {
    console.error('[relationshipMappingApi] v2 fetch failed', e)
    return null
  }
}
